/* @refresh skip */
import React, { useState, useMemo, useEffect } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from "libphonenumber-js";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// --- 1. Génération de la liste complète des pays ---
// On le fait en dehors du composant pour ne le calculer qu'une seule fois au chargement
const countryList = getCountries()
  .map((country) => {
    // Traducteur natif du navigateur (en français ici)
    const regionName = new Intl.DisplayNames(["fr"], { type: "region" });

    try {
      return {
        value: country,
        label: regionName.of(country) || country, // "France", "États-Unis"...
        callingCode: `+${getCountryCallingCode(country)}`,
      };
    } catch (e) {
      return null;
    }
  })
  .filter(
    (c): c is { value: CountryCode; label: string; callingCode: string } =>
      c !== null
  )
  // On trie alphabétiquement par le nom français (A-Z)
  .sort((a, b) => a.label.localeCompare(b.label));

// --- Composant Drapeau (Optimisé) ---
const Flag = ({
  countryCode,
  className,
}: {
  countryCode: string;
  className?: string;
}) => (
  <img
    src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${countryCode.toLowerCase()}.png 2x`}
    alt={`Drapeau ${countryCode}`}
    className={cn("h-4 w-6 object-cover rounded-[2px]", className)}
    loading="lazy"
  />
);

interface PhoneInputProps {
  onPhoneChange?: (data: {
    formatted: string;
    e164: string | null;
    isValid: boolean;
    country: CountryCode;
  }) => void;
  defaultCountry?: CountryCode;
}

export default function PhoneInputFull({
  onPhoneChange,
  defaultCountry = "FR",
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  const [selectedIso, setSelectedIso] = useState<CountryCode>(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    setSelectedIso(defaultCountry);
  }, [defaultCountry]);

  // Récupérer le code d'appel actuel pour l'affichage du bouton
  const selectedCountryData = useMemo(
    () => countryList.find((c) => c.value === selectedIso),
    [selectedIso]
  );

  const handlePhoneUpdate = (inputValue: string, country: CountryCode) => {
    const asYouType = new AsYouType(country);
    const formatted = asYouType.input(inputValue);

    let e164: string | null = null;
    let isValid = false;

    try {
      const parsed = parsePhoneNumber(inputValue, country);
      if (parsed) {
        isValid = parsed.isValid();
        e164 = parsed.number;
      }
    } catch {
      // ignore parsing errors for partial input
    }

    setPhoneNumber(formatted);

    if (onPhoneChange) {
      onPhoneChange({
        formatted,
        e164,
        isValid,
        country,
      });
    }
  };

  return (
    <div className="space-y-1.5 w-full max-w-sm">
      <Label className="text-xs text-white/80 uppercase ml-1 font-medium tracking-wide">
        Téléphone
      </Label>

      <div className="flex gap-2">
        {/* --- SELECTEUR PAYS --- */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[125px] h-11 justify-between bg-white/10 border-white/10 text-white hover:bg-white/20 hover:text-white rounded-xl transition-all border-transparent focus:ring-2 focus:ring-[#FF9F68] px-3"
            >
              <span className="flex items-center gap-2 truncate">
                <Flag
                  countryCode={selectedIso}
                  className="h-3.5 w-5 shrink-0"
                />
                <span className="text-sm font-semibold opacity-90">
                  {selectedCountryData?.callingCode || "+??"}
                </span>
              </span>
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[300px] p-0 bg-[#1a1a1a] border-white/10 text-white rounded-xl shadow-2xl backdrop-blur-xl ring-1 ring-white/10"
            align="start"
          >
            {/* L'option 'filter' ici permet de chercher dans label, value et callingCode */}
            <Command
              className="bg-transparent"
              filter={(value, search) => {
                const country = countryList.find((c) => c.label === value);
                if (!country) return 0;
                // Logique de recherche personnalisée : Nom ou Code (+33) ou ISO (FR)
                const searchLower = search.toLowerCase();
                const matchesName = country.label
                  .toLowerCase()
                  .includes(searchLower);
                const matchesCode = country.callingCode.includes(searchLower);
                const matchesIso = country.value
                  .toLowerCase()
                  .includes(searchLower);

                return matchesName || matchesCode || matchesIso ? 1 : 0;
              }}
            >
              <div className="flex items-center border-b border-white/10 px-3">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <CommandInput
                  placeholder="Rechercher (ex: France, +33)..."
                  className="placeholder:text-white/30 text-white h-10 bg-transparent border-none focus:ring-0 text-sm"
                />
              </div>

              {/* Virtualisation légère : on limite la hauteur pour la perf si la liste est longue */}
              <CommandList className="max-h-[250px] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                <CommandEmpty className="py-4 text-center text-xs text-white/40">
                  Aucun pays trouvé.
                </CommandEmpty>
                <CommandGroup>
                  {countryList.map((country) => (
                    <CommandItem
                      key={country.value}
                      value={country.label} // Utilisé par le filtre interne de Command
                      onSelect={() => {
                        setSelectedIso(country.value);
                        setOpen(false);
                        handlePhoneUpdate(phoneNumber, country.value);
                      }}
                      className="flex items-center gap-3 cursor-pointer text-white aria-selected:bg-white/10 aria-selected:text-white rounded-lg px-2 py-2 transition-colors mb-0.5"
                    >
                      <Flag
                        countryCode={country.value}
                        className="h-4 w-6 shadow-sm shrink-0"
                      />
                      <span className="flex-1 truncate text-sm">
                        {country.label}
                      </span>
                      <span className="text-white/40 text-xs font-mono">
                        {country.callingCode}
                      </span>

                      {selectedIso === country.value && (
                        <Check className="ml-auto h-4 w-4 text-[#FF9F68]" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* --- INPUT --- */}
        <div className="relative flex-1">
          <Input
            type="tel"
            placeholder="06 12 34 56 78"
            value={phoneNumber}
            onChange={(e) => handlePhoneUpdate(e.target.value, selectedIso)}
            className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 border-transparent transition-all pl-3"
          />
        </div>
      </div>
    </div>
  );
}
